import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSEO from "@/hooks/useSEO";

const AnimatedSection = ({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

const steps = [
  {
    num: "01",
    icon: "ri-chat-3-line",
    title: "無料相談・ヒアリング",
    titleEn: "Free Consultation",
    desc: "まずはお気軽にお問い合わせください。現在の業務課題や運用状況をヒアリングし、最適なソリューションをご提案するための情報収集を行います。オンライン・対面どちらでも対応可能です。",
    duration: "1〜2営業日",
    color: "cyan",
  },
  {
    num: "02",
    icon: "ri-file-list-3-line",
    title: "要件定義・提案書作成",
    titleEn: "Requirements & Proposal",
    desc: "ヒアリング内容をもとに、業務フローの分析と要件定義を行います。最適なシステム構成・サービス範囲・費用感を含む提案書を作成し、ご説明いたします。",
    duration: "3〜5営業日",
    color: "violet",
  },
  {
    num: "03",
    icon: "ri-settings-3-line",
    title: "契約・システム設定",
    titleEn: "Contract & Setup",
    desc: "ご契約後、システムの初期設定・カスタマイズを開始します。銀行APIとの接続設定、データ連携の構築、ダッシュボードのカスタマイズなどを行います。",
    duration: "1〜2週間",
    color: "emerald",
  },
  {
    num: "04",
    icon: "ri-test-tube-line",
    title: "テスト・検証・トレーニング",
    titleEn: "Testing & Training",
    desc: "本番環境に近い条件でのテストを実施し、照合精度・処理速度・セキュリティを検証します。担当者向けのトレーニングも実施し、スムーズな運用開始を支援します。",
    duration: "1〜2週間",
    color: "amber",
  },
  {
    num: "05",
    icon: "ri-rocket-line",
    title: "本番稼働・運用開始",
    titleEn: "Go Live",
    desc: "テスト完了後、本番環境での稼働を開始します。稼働開始後も専任サポートチームが24時間365日体制でモニタリングし、安定した運用をサポートします。",
    duration: "稼働開始",
    color: "rose",
  },
  {
    num: "06",
    icon: "ri-customer-service-2-line",
    title: "継続サポート・改善",
    titleEn: "Ongoing Support",
    desc: "運用開始後も定期的なレビューを実施し、業務の変化に合わせたシステムの改善・機能追加を継続的に行います。お客様の成長とともにシステムも進化します。",
    duration: "継続的",
    color: "sky",
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; darkBg: string; darkText: string }> = {
  cyan:    { bg: "bg-cyan-50",    text: "text-cyan-600",    border: "border-cyan-200",    darkBg: "bg-cyan-500",    darkText: "text-cyan-400" },
  violet:  { bg: "bg-violet-50",  text: "text-violet-600",  border: "border-violet-200",  darkBg: "bg-violet-500",  darkText: "text-violet-400" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", darkBg: "bg-emerald-500", darkText: "text-emerald-400" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-600",   border: "border-amber-200",   darkBg: "bg-amber-500",   darkText: "text-amber-400" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-600",    border: "border-rose-200",    darkBg: "bg-rose-500",    darkText: "text-rose-400" },
  sky:     { bg: "bg-sky-50",     text: "text-sky-600",     border: "border-sky-200",     darkBg: "bg-sky-500",     darkText: "text-sky-400" },
};

const faq = [
  { q: "導入までどのくらいの期間がかかりますか？", a: "標準的なケースで3〜6週間程度です。業務の複雑さや連携するシステムの数によって変動しますが、最短2週間での稼働実績もございます。" },
  { q: "既存の会計システムと連携できますか？", a: "はい、主要な会計ソフト・ERPとのAPI連携に対応しています。詳細はヒアリング時にご確認ください。" },
  { q: "セキュリティ面は安全ですか？", a: "金融グレードのセキュリティ基準に準拠したクラウドインフラを使用しており、データの暗号化・アクセス制御・監査ログを完備しています。" },
  { q: "小規模な企業でも導入できますか？", a: "はい、企業規模を問わずご利用いただけます。業務量に応じたプランをご用意しておりますので、まずはご相談ください。" },
];

const IntroductionFlowPage = () => {
  const { t } = useTranslation();

  useSEO({
    title: "導入の流れ | 相談から運用開始まで6ステップ",
    description: "Stream Solutions Limitedの導入プロセス。無料相談・要件定義・システム設定・テスト・本番稼働・継続サポートの6ステップで、決済管理システムをスムーズに導入できます。",
    keywords: "決済管理導入,システム導入フロー,フィンテック導入,消込システム導入,導入支援",
    canonical: "/introduction-flow",
    ogTitle: "導入の流れ | Stream Solutions Limited",
    ogDescription: "ご相談から運用開始まで最短2週間。専任チームが丁寧にサポートします。",
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract-digital-workflow-process-visualization-dark-background-with-connected-nodes-flowing-data-streams-step-by-step-progression-cyan-accent-lights-futuristic-fintech-atmosphere-no-people-clean-minimal&width=1920&height=600&seq=flowhero001&orientation=landscape"
            alt="flow hero"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 to-slate-950" />
        </div>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "linear-gradient(rgba(6,182,212,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-cyan-500" />
            <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">HOW IT WORKS</span>
            <span className="w-8 h-px bg-cyan-500" />
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">導入の流れ</h1>
          <p className="text-slate-400 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            ご相談から運用開始まで、専任チームが丁寧にサポートします。
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative">
            {/* Vertical line */}
            <div className="hidden lg:block absolute left-[2.75rem] top-0 bottom-0 w-px bg-gradient-to-b from-cyan-200 via-slate-200 to-transparent" />

            <div className="space-y-12">
              {steps.map((step, i) => {
                const c = colorMap[step.color];
                return (
                  <AnimatedSection key={step.num} delay={i * 0.1}>
                    <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">
                      {/* Icon circle */}
                      <div className="flex-shrink-0 flex flex-col items-center">
                        <div className={`w-22 h-22 w-[5.5rem] h-[5.5rem] flex items-center justify-center rounded-full ${c.bg} border-2 ${c.border} relative z-10`}>
                          <i className={`${step.icon} ${c.text} text-2xl`} />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          <span className={`text-xs font-bold tracking-widest ${c.text} uppercase`}>
                            STEP {step.num}
                          </span>
                          <span className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                            {step.duration}
                          </span>
                        </div>
                        <h2 className="text-xl lg:text-2xl font-bold text-slate-900 mb-2">{step.title}</h2>
                        <p className="text-xs text-slate-400 mb-3 tracking-wide">{step.titleEn}</p>
                        <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="w-8 h-px bg-cyan-500" />
                <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">FAQ</span>
                <span className="w-8 h-px bg-cyan-500" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">よくあるご質問</h2>
            </div>
          </AnimatedSection>
          <div className="space-y-4">
            {faq.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="p-6 rounded-2xl border border-slate-800 bg-slate-900/60">
                  <div className="flex items-start gap-4">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-cyan-500/20 flex-shrink-0 mt-0.5">
                      <span className="text-cyan-400 text-xs font-bold">Q</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-3">{item.q}</p>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-700 flex-shrink-0">
                          <span className="text-slate-300 text-xs font-bold">A</span>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed">{item.a}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">{t("top.cta.title")}</h2>
            <p className="text-slate-600 mb-8">{t("top.cta.sub")}</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {t("top.cta.btn")}
              <i className="ri-arrow-right-line" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IntroductionFlowPage;
